import React, { useState } from 'react';

const CareerGuide = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [completedGuides, setCompletedGuides] = useState([1, 4, 7]);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [showGuideModal, setShowGuideModal] = useState(false);

  // Expanded guide content data
  const guideContent = {
    1: {
      title: 'ATS Optimization Guide',
      category: 'resume',
      duration: '15 min',
      level: 'Beginner',
      description: 'Learn how to make your resume pass through Applicant Tracking Systems',
      content: `
# ATS Optimization Guide

## What is an ATS?
Applicant Tracking Systems (ATS) are software used by employers to manage job applications. They scan and filter resumes before they reach human eyes.

## Key ATS Optimization Tips

### 1. Use Standard Section Headings
- **Use**: Work Experience, Education, Skills
- **Avoid**: Creative titles like "My Journey" or "Professional Odyssey"

### 2. Keyword Optimization
- Include keywords from the job description
- Use both acronyms and full forms (e.g., "JavaScript" and "JS")
- Mention specific technologies and tools

### 3. File Format Matters
- **Best**: .docx or .pdf (text-based)
- **Avoid**: Images, tables, and columns

### 4. Simple Formatting
- Use standard fonts (Arial, Calibri, Times New Roman)
- Avoid graphics, images, and complex layouts
- Use bullet points instead of paragraphs

## Action Steps

1. **Review your current resume** against the job description
2. **Identify missing keywords** and incorporate them
3. **Test your resume** with free ATS checkers
4. **Save in the correct format** before applying

## Common ATS Mistakes to Avoid

❌ Using headers and footers
❌ Putting important info in tables
❌ Creative/design-heavy templates
❌ Missing contact information
      `,
      steps: [
        'Analyze job description for keywords',
        'Update your resume with relevant keywords',
        'Use standard section headings',
        'Remove complex formatting and tables',
        'Save as ATS-friendly format (.docx or text-based PDF)'
      ]
    },
    2: {
      title: 'Resume Template Library',
      category: 'resume',
      duration: '10 min',
      level: 'All Levels',
      description: 'Choose from 20+ industry-specific resume templates',
      content: `
# Resume Template Library

## Choose the Right Template for Your Industry

### Technical Roles (Developers, Engineers)
- Clean, minimal design
- Skills section prominently featured
- Project portfolio highlights
- GitHub and technical links

### Creative Roles (Designers, Marketers)
- Visually appealing but still ATS-friendly
- Portfolio links emphasized
- Color used strategically
- Project case studies

### Business & Corporate Roles
- Professional, conservative design
- Achievement-focused
- Leadership experience highlighted
- Education and certifications featured

## Template Categories Available

### 1. Modern Minimalist
- Clean lines and plenty of white space
- Perfect for tech and startup roles
- ATS-optimized while looking contemporary

### 2. Professional Executive
- Traditional but polished
- Ideal for corporate environments
- Emphasis on experience and leadership

### 3. Creative Portfolio
- Visually engaging while readable
- Great for designers and creatives
- Balanced design and content

## How to Choose

Consider:
- Your industry norms
- Company culture
- Your experience level
- ATS requirements

**Pro Tip**: Always have an ATS-friendly version ready, even if you use a creative template for direct applications.
      `,
      steps: [
        'Identify your industry and role type',
        'Browse templates in your category',
        'Choose 2-3 templates that fit your style',
        'Customize with your information',
        'Test for ATS compatibility'
      ]
    },
    // Add more guide content as needed...
  };

  const careerCategories = [
    {
      id: 'resume',
      title: 'Resume Builder',
      icon: 'fas fa-file-alt',
      color: 'blue',
      description: 'Create ATS-friendly resumes and cover letters',
      progress: 65,
      guides: [
        { id: 1, ...guideContent[1] },
        { id: 2, ...guideContent[2] },
        {
          id: 3,
          title: 'Cover Letter Mastery',
          duration: '20 min',
          level: 'Intermediate',
          description: 'Write compelling cover letters that get interviews',
          content: 'Cover letter content would go here...',
          steps: ['Step 1', 'Step 2', 'Step 3']
        }
      ]
    },
    // ... other categories remain the same
  ];

  const featuredTools = [
    {
      id: 1,
      title: 'Resume Score Checker',
      description: 'Get instant feedback on your resume',
      icon: 'fas fa-chart-bar',
      action: () => alert('Resume Score Checker would open here')
    },
    {
      id: 2,
      title: 'Interview Question Generator',
      description: 'Practice with role-specific questions',
      icon: 'fas fa-question-circle',
      action: () => alert('Interview Question Generator would open here')
    },
    {
      id: 3,
      title: 'Salary Calculator',
      description: 'Research market rates for your role',
      icon: 'fas fa-calculator',
      action: () => alert('Salary Calculator would open here')
    },
    {
      id: 4,
      title: 'Skill Gap Analyzer',
      description: 'Identify skills to develop next',
      icon: 'fas fa-search',
      action: () => alert('Skill Gap Analyzer would open here')
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      purple: 'bg-purple-100 text-purple-600',
      orange: 'bg-orange-100 text-orange-600',
      indigo: 'bg-indigo-100 text-indigo-600',
      red: 'bg-red-100 text-red-600',
      teal: 'bg-teal-100 text-teal-600',
      pink: 'bg-pink-100 text-pink-600'
    };
    return colorMap[color] || 'bg-gray-100 text-gray-600';
  };

  const toggleGuideCompletion = (guideId) => {
    if (completedGuides.includes(guideId)) {
      setCompletedGuides(completedGuides.filter(id => id !== guideId));
    } else {
      setCompletedGuides([...completedGuides, guideId]);
    }
  };

  const handleStartGuide = (guide) => {
    setSelectedGuide(guide);
    setShowGuideModal(true);
  };

  const handleCompleteGuide = (guideId) => {
    if (!completedGuides.includes(guideId)) {
      setCompletedGuides([...completedGuides, guideId]);
    }
    setShowGuideModal(false);
  };

  const filteredCategories = activeCategory === 'all' 
    ? careerCategories 
    : careerCategories.filter(cat => cat.id === activeCategory);

  const totalGuides = careerCategories.reduce((acc, cat) => acc + cat.guides.length, 0);
  const completedCount = completedGuides.length;
  const progressPercentage = Math.round((completedCount / totalGuides) * 100);

  // Format guide content with basic markdown
  const formatGuideContent = (content) => {
    return content.split('\n').map((line, index) => {
      if (line.startsWith('# ')) {
        return <h1 key={index} className="text-2xl font-bold text-gray-800 mt-6 mb-4">{line.substring(2)}</h1>;
      } else if (line.startsWith('## ')) {
        return <h2 key={index} className="text-xl font-bold text-gray-800 mt-5 mb-3">{line.substring(3)}</h2>;
      } else if (line.startsWith('### ')) {
        return <h3 key={index} className="text-lg font-semibold text-gray-800 mt-4 mb-2">{line.substring(4)}</h3>;
      } else if (line.startsWith('- **')) {
        const parts = line.split('**');
        return (
          <li key={index} className="flex items-start ml-4 mb-1">
            <span className="mr-2">•</span>
            <strong>{parts[1]}</strong>
            {parts[2]}
          </li>
        );
      } else if (line.startsWith('- ')) {
        return <li key={index} className="flex items-start ml-4 mb-1"><span className="mr-2">•</span>{line.substring(2)}</li>;
      } else if (line.startsWith('❌')) {
        return <div key={index} className="flex items-start text-red-600 mb-1"><i className="fas fa-times mr-2 mt-1"></i>{line.substring(2)}</div>;
      } else if (line.startsWith('✅')) {
        return <div key={index} className="flex items-start text-green-600 mb-1"><i className="fas fa-check mr-2 mt-1"></i>{line.substring(2)}</div>;
      } else if (line.trim() === '') {
        return <br key={index} />;
      } else {
        return <p key={index} className="mb-3 text-gray-700">{line}</p>;
      }
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Career Guide</h1>
        <p className="text-gray-400">Your complete roadmap to career success with step-by-step guides and tools</p>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <i className="fas fa-book-open text-blue-500 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Guides</p>
              <p className="text-2xl font-bold text-gray-900">{totalGuides}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <i className="fas fa-check-circle text-green-500 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{completedCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <i className="fas fa-trophy text-purple-500 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Progress</p>
              <p className="text-2xl font-bold text-gray-900">{progressPercentage}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 rounded-lg">
              <i className="fas fa-clock text-orange-500 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Time Saved</p>
              <p className="text-2xl font-bold text-gray-900">12h</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Content - 3/4 width */}
        <div className="lg:col-span-3">
          {/* Category Filters */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveCategory('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === 'all'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Categories
              </button>
              {careerCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeCategory === category.id
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.title}
                </button>
              ))}
            </div>
          </div>

          {/* Career Guide Categories */}
          <div className="space-y-6">
            {filteredCategories.map((category) => (
              <div key={category.id} className="bg-white rounded-lg shadow-md">
                {/* Category Header */}
                <div className="border-b border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getColorClasses(category.color)}`}>
                        <i className={`${category.icon} text-lg`}></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">{category.title}</h3>
                        <p className="text-gray-400">{category.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm text-gray-600">Progress</span>
                        <span className="text-sm font-medium text-gray-800">{category.progress}%</span>
                      </div>
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${category.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Guides List */}
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {category.guides.map((guide) => (
                      <div key={guide.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-semibold text-gray-800">{guide.title}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                {guide.duration}
                              </span>
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                {guide.level}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => toggleGuideCompletion(guide.id)}
                            className={`p-2 rounded-full ${
                              completedGuides.includes(guide.id)
                                ? 'bg-green-100 text-green-500'
                                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                            }`}
                          >
                            <i className={`fas ${completedGuides.includes(guide.id) ? 'fa-check-circle' : 'fa-circle'} text-sm`}></i>
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">{guide.description}</p>
                        <button 
                          onClick={() => handleStartGuide(guide)}
                          className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
                        >
                          Start Guide
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar - 1/4 width */}
        <div className="lg:col-span-1">
          {/* Quick Tools */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="font-semibold text-gray-800 mb-4">Quick Tools</h3>
            <div className="space-y-3">
              {featuredTools.map((tool) => (
                <button
                  key={tool.id}
                  onClick={tool.action}
                  className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className={`${tool.icon} text-blue-500`}></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{tool.title}</p>
                      <p className="text-xs text-gray-600">{tool.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Recommended Next Steps */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Recommended Next</h3>
            <div className="space-y-3">
              {careerCategories
                .flatMap(cat => cat.guides)
                .filter(guide => !completedGuides.includes(guide.id))
                .slice(0, 3)
                .map((guide) => (
                  <div key={guide.id} className="p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <i className="fas fa-play text-blue-500 text-sm"></i>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 text-sm">{guide.title}</p>
                        <p className="text-xs text-gray-500">{guide.duration}</p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Progress Tips */}
            <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-medium text-green-800 text-sm mb-2 flex items-center gap-2">
                <i className="fas fa-trophy"></i>
                Pro Tip
              </h4>
              <p className="text-xs text-green-700">
                Complete 3 guides this week to unlock advanced career coaching features!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Guide Modal */}
      {showGuideModal && selectedGuide && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white">{selectedGuide.title}</h2>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {selectedGuide.duration}
                    </span>
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {selectedGuide.level}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setShowGuideModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <div className="prose max-w-none">
                {formatGuideContent(selectedGuide.content)}
              </div>

              {selectedGuide.steps && (
                <div className="mt-8 p-6 bg-blue-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-800 mb-4">Action Steps</h3>
                  <div className="space-y-3">
                    {selectedGuide.steps.map((step, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </div>
                        <span className="text-blue-800">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 justify-end mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowGuideModal(false)}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800"
                >
                  Close
                </button>
                <button
                  onClick={() => handleCompleteGuide(selectedGuide.id)}
                  className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors"
                >
                  Mark as Complete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareerGuide;